import { TypographyVariant } from '@mui/material';
import { TypographyProps } from '../components';
import { useTheme } from '../theme/useTheme';

// 'null' is when the browser doesn't support the API, `undefined` is we haven't initialized it yet
let canvasContext: CanvasRenderingContext2D | null | undefined = undefined;

/**
 * Measures the dimensions of some text using a 2d canvas context. Returns `undefined` if the browser doesn't support
 * this API. Optionally, set the `font` to use when measuring which can be any string which works with the CSS `font`
 * property (see: https://developer.mozilla.org/en-US/docs/Web/CSS/font).
 */
export function measureText(text: string, font = '') {
  // Init if we haven't yet
  if (canvasContext === undefined) {
    canvasContext = document.createElement('canvas').getContext('2d');
  }

  // Browser doesn't support '2d' context
  if (canvasContext === null) {
    return undefined;
  }

  canvasContext.font = font;
  return canvasContext.measureText(text);
}

const remValueRegex = /^([\d.]+)rem$/;

/**
 * Takes in a style value for measurement. If it is measured in rems, it is
 * converted to pixels. Otherwise, the original value is returned.
 */
// Using unknown for the `styleValue` here to avoid trying to fight with all the
// fiddly different types that can exist in the MUI theme. We do type guarding to
// only cover the plain string use case, so this should still be type-safe.
export function normalizeRemToPx(styleValue: unknown, htmlFontSize: number) {
  if (typeof styleValue !== 'string') {
    return styleValue;
  }

  const remMatch = styleValue.match(remValueRegex);
  const digits = remMatch?.[1];
  if (remMatch && digits) {
    const valueInRem = parseFloat(digits);
    const valueInPx = valueInRem * htmlFontSize;
    return `${valueInPx}px`;
  }

  return styleValue;
}

/**
 * Takes in a theme typography variant and returns a `font` attribute suitable for using with `measureText`.
 *
 * Note that this utility will translate rem-based measurements to pixel-based measurement to work around a
 * bug in safari.
 */
export function useTypographyVariantFontForMeasuringText(typographyVariant: Required<TypographyProps>['variant']) {
  const { theme } = useTheme();
  const htmlFontSize = theme.typography.htmlFontSize;

  const themeVariant = theme.typography[typographyVariant as TypographyVariant];

  // Normalizing these values to use px (instead of rem) is necessary for safari to work properly.
  // Most browsers work fine because they evaluate rem-based fonts in canvas based on the `html`
  // element (just like how rem works on other elements). For some reason, safari appears to instead
  // evaluate rem-based canvas font sizes based on the parent of the canvas.
  const fontSize = normalizeRemToPx(themeVariant.fontSize, htmlFontSize);
  const lineHeight = normalizeRemToPx(themeVariant.lineHeight, htmlFontSize);

  // Note that canvas does not support the `letter-spacing` css setting, and it cannot be included in
  // `font` shorthand, so it is not included even if specified on a variant.
  const font = themeVariant ? `${themeVariant.fontWeight} ${fontSize}/${lineHeight} ${themeVariant.fontFamily}` : '';

  return font;
}

/**
 * Hook-based variation of `measureText` that can be passed a chrono-ui `Typography` variant that will automatically be
 * used to set the `font`.
 */
export function useMeasureTextWithTypographyVariant(
  text: string,
  typographyVariant: Required<TypographyProps>['variant']
) {
  const font = useTypographyVariantFontForMeasuringText(typographyVariant);
  return measureText(text, font);
}
